import type { Control, FieldErrors, FieldValues, Path } from "react-hook-form";
import { useController, useFormState } from "react-hook-form";

import type { Currency } from "@krea/shared/types/common";

import { EarlyRepaymentFeeInput } from "./EarlyRepaymentFeeInput";
import type { IEarlyRepayment } from "./types";

interface IControlledEarlyRepaymentFeeInputProps<T extends FieldValues> {
  control: Control<T>;
  bidFormIndex: number;
  currency: Currency;
  disabled?: boolean;
}

/**
 * Nothing chosen yet. Deliberately NOT `FREE_EARLY_REPAYMENT`: a bid whose
 * terms never loaded must read as "nothing chosen", not as free of charge.
 */
const NOTHING_CHOSEN: IEarlyRepayment = {
  type: null,
  unit: null,
  value: null,
  paidPeriodMonths: null,
  comment: null,
};

/**
 * React Hook Form binding for `EarlyRepaymentFeeInput`. Shared because lender
 * and admin both need exactly this and apps cannot import from apps; both
 * address the terms under a `bids` array, hence the built-in path base.
 *
 * One controller for the whole object, not one per member. Besides matching
 * the wire, it makes a type change ATOMIC: the previous five-field version
 * fired five `onChange`s in sequence, and the resolver ran against each
 * intermediate state — so switching to FREE briefly validated
 * `{type: FREE, value: 5}` and could flash "a fee applies only to…".
 */
export const ControlledEarlyRepaymentFeeInput = <T extends FieldValues>({
  control,
  bidFormIndex,
  currency,
  disabled = false,
}: IControlledEarlyRepaymentFeeInputProps<T>) => {
  const { field, fieldState } = useController({
    name: `bids.${bidFormIndex}.earlyRepayment` as Path<T>,
    control,
  });

  // The input hides an error until its own member has been used, so that
  // choosing a type does not immediately redden the sub-fields it reveals.
  // A submit attempt has to override that, or a blocked save would report
  // nothing at all.
  const { isSubmitted } = useFormState({ control });

  // RHF stores validation errors at LEAF paths (`bids.0.earlyRepayment.value`)
  // and `getFieldState` is a plain path walk over `formState.errors` — so for
  // an OBJECT field it hands back the map of those leaves, not a single
  // `FieldError`. The declared type doesn't model object-valued fields, and
  // the two shapes collide on the key literally named `type`, hence the
  // double assertion rather than a direct one.
  //
  // Verified end to end, not just reasoned about: LoanForm.test.tsx's "shows a
  // nested early-repayment error on the field it belongs to" drives the real
  // resolver and goes red if this reads `fieldState.error.message` — the way
  // the declared type says it should work.
  const leafErrors = fieldState.error as unknown as
    | FieldErrors<IEarlyRepayment>
    | undefined;

  return (
    <EarlyRepaymentFeeInput
      value={(field.value as IEarlyRepayment | undefined) ?? NOTHING_CHOSEN}
      onChange={field.onChange}
      currency={currency}
      idPrefix={`bid-${bidFormIndex}`}
      disabled={disabled}
      showAllErrors={isSubmitted}
      errors={{
        type: leafErrors?.type?.message,
        unit: leafErrors?.unit?.message,
        value: leafErrors?.value?.message,
        paidPeriodMonths: leafErrors?.paidPeriodMonths?.message,
      }}
    />
  );
};
